import { useEffect, useState } from "react";
import { Skeleton } from "@/components/skeleton";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency } from "@/lib/i18n";

export interface CrmRelationshipNode {
  id: string;
  type: "person" | "company";
  label: string;
  metadata?: {
    leadScore?: number;
    dealValueCents?: number;
    referralCount?: number;
    isConnector?: boolean;
  };
}

export interface CrmRelationshipEdge {
  source: string;
  target: string;
  type: string;
  label: string;
  weight: number;
}

export interface CrmRelationshipGraph {
  centerNodeId: string;
  nodes: CrmRelationshipNode[];
  edges: CrmRelationshipEdge[];
  connectors: CrmRelationshipNode[];
}

interface CrmRelationshipMapProps {
  contactId: string;
  currency?: string;
}

interface CrmRelationshipGraphViewProps {
  graph: CrmRelationshipGraph;
  currency?: string;
}

export function CrmRelationshipMap({
  contactId,
  currency = "EUR",
}: CrmRelationshipMapProps) {
  const [graph, setGraph] = useState<CrmRelationshipGraph | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    apiFetch<{ data: CrmRelationshipGraph }>(`/crm/people/${contactId}/relationships`)
      .then((response) => {
        if (!cancelled) {
          setGraph(response.data);
        }
      })
      .catch((caughtError) => {
        if (!cancelled) {
          setGraph(null);
          setError(caughtError instanceof Error ? caughtError.message : "Failed to load relationships");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [contactId]);

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (error) {
    return <p className="text-sm text-[var(--color-destructive)]">{error}</p>;
  }

  if (!isRenderableRelationshipGraph(graph)) {
    return <p className="text-sm text-[var(--color-muted-foreground)]">No relationship evidence available for this contact yet.</p>;
  }

  return <CrmRelationshipGraphView graph={graph} currency={currency} />;
}

export function CrmRelationshipGraphView({
  graph,
  currency = "EUR",
}: CrmRelationshipGraphViewProps) {
  const centerNode = graph.nodes.find((node) => node.id === graph.centerNodeId);
  const relatedNodes = graph.nodes.filter((node) => node.id !== graph.centerNodeId);
  const nodeLookup = new Map(graph.nodes.map((node) => [node.id, node]));
  const connectorIds = new Set(graph.connectors.map((node) => node.id));
  const referralEdges = graph.edges.filter((edge) => edge.type === "referral");
  const influentialNodes = [...relatedNodes]
    .sort((left, right) => getCrmRelationshipInfluenceScore(right) - getCrmRelationshipInfluenceScore(left))
    .slice(0, 4);
  const orbitRadius = 132;
  const positionedNodes = relatedNodes.map((node, index) => {
    const angle = (index / Math.max(relatedNodes.length, 1)) * Math.PI * 2 - Math.PI / 2;
    return {
      ...node,
      x: 160 + Math.cos(angle) * orbitRadius,
      y: 160 + Math.sin(angle) * orbitRadius,
    };
  });

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <p className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Relationship center</p>
        <p className="mt-2 text-base font-semibold text-[var(--color-foreground)]">{centerNode?.label ?? "Unknown contact"}</p>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          {graph.edges.length} live connection{graph.edges.length === 1 ? "" : "s"} mounted from the CRM graph endpoint.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <RelationshipMetricCard
          label="Connectors"
          value={graph.connectors.length}
          description={graph.connectors.length > 0 ? "People with multiple referral edges in this graph." : "No connector evidence yet."}
        />
        <RelationshipMetricCard
          label="Referral paths"
          value={referralEdges.length}
          description={referralEdges.length > 0 ? "Mounted introductions or referral chains touching this contact." : "No referral paths mounted yet."}
        />
        <RelationshipMetricCard
          label="Influence signals"
          value={influentialNodes.filter((node) => getCrmRelationshipInfluenceScore(node) > 0).length}
          description="Ranked from real lead score, referral count, connector status, and attached deal value."
        />
      </div>
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <svg viewBox="0 0 320 320" className="mx-auto h-[320px] w-full max-w-[320px]">
          {positionedNodes.map((node) => (
            <g key={`edge-${node.id}`}>
              <line
                x1={160}
                y1={160}
                x2={node.x}
                y2={node.y}
                stroke="var(--color-border)"
                strokeWidth="2"
              />
              <text
                x={(160 + node.x) / 2}
                y={(160 + node.y) / 2}
                textAnchor="middle"
                className="fill-[var(--color-muted-foreground)] text-[8px] uppercase"
              >
                {graph.edges.find((edge) => edge.source === node.id || edge.target === node.id)?.type.replaceAll("_", " ")}
              </text>
              <circle cx={node.x} cy={node.y} r="26" fill="var(--color-primary-light)" stroke="var(--color-primary)" strokeWidth="2" />
              <text x={node.x} y={node.y - 2} textAnchor="middle" className="fill-[var(--color-foreground)] text-[10px] font-semibold">
                {node.label.slice(0, 14)}
              </text>
              <text x={node.x} y={node.y + 12} textAnchor="middle" className="fill-[var(--color-muted-foreground)] text-[9px] uppercase">
                {node.type}
              </text>
            </g>
          ))}
          <circle cx="160" cy="160" r="34" fill="var(--color-card)" stroke="var(--color-primary)" strokeWidth="3" />
          <text x="160" y="156" textAnchor="middle" className="fill-[var(--color-foreground)] text-[12px] font-semibold">
            {centerNode?.label?.slice(0, 16) ?? "Contact"}
          </text>
          <text x="160" y="172" textAnchor="middle" className="fill-[var(--color-muted-foreground)] text-[10px] uppercase">
            center
          </text>
        </svg>
      </div>
      {graph.connectors.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Connector detection</p>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Contacts with multiple referral edges around this relationship graph.
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {graph.connectors.map((connector) => (
              <span
                key={connector.id}
                className="rounded-full bg-[var(--color-primary-light)] px-3 py-1 text-xs font-medium text-[var(--color-primary)]"
              >
                {connector.label}
              </span>
            ))}
          </div>
        </div>
      )}
      {referralEdges.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <p className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Referral paths</p>
          <div className="mt-3 space-y-2">
            {referralEdges.map((edge) => (
              <div key={`${edge.source}-${edge.target}-${edge.label}`} className="rounded-lg bg-[var(--color-surface)] px-3 py-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">{nodeLookup.get(edge.source)?.label ?? "Unknown"}</span>
                <span className="mx-2 text-[var(--color-muted-foreground)]">-&gt;</span>
                <span className="font-medium text-[var(--color-foreground)]">{nodeLookup.get(edge.target)?.label ?? "Unknown"}</span>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {edge.label} - strength {edge.weight}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      {influentialNodes.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <p className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Influence mapping</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {influentialNodes.map((node) => (
              <div key={node.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">{node.label}</p>
                    <p className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">{node.type}</p>
                  </div>
                  <span className="rounded-full bg-[var(--color-primary-light)] px-2 py-1 text-[11px] font-medium text-[var(--color-primary)]">
                    Influence {getCrmRelationshipInfluenceScore(node)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-[var(--color-muted-foreground)]">
                  {node.metadata?.isConnector || connectorIds.has(node.id) ? (
                    <span className="rounded-full bg-[var(--color-primary-light)] px-2 py-1 text-[var(--color-primary)]">Connector</span>
                  ) : null}
                  {typeof node.metadata?.referralCount === "number" ? <span>{node.metadata.referralCount} referrals</span> : null}
                  {typeof node.metadata?.leadScore === "number" ? <span>Lead score {node.metadata.leadScore}</span> : null}
                  {typeof node.metadata?.dealValueCents === "number" ? (
                    <span>Deal value {formatCurrency(node.metadata.dealValueCents, currency, { maximumFractionDigits: 0 })}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid gap-3">
        {relatedNodes.map((node) => {
          const matchingEdges = graph.edges.filter((edge) => edge.source === node.id || edge.target === node.id);
          return (
            <div key={node.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[var(--color-foreground)]">{node.label}</p>
                  <p className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">{node.type}</p>
                </div>
                <div className="flex items-center gap-2">
                  {(node.metadata?.isConnector || connectorIds.has(node.id)) && (
                    <span className="rounded-full bg-[var(--color-primary-light)] px-2.5 py-1 text-xs text-[var(--color-primary)]">
                      Connector
                    </span>
                  )}
                  <span className="rounded-full bg-[var(--color-primary-light)] px-2.5 py-1 text-xs text-[var(--color-primary)]">
                    {matchingEdges.length} link{matchingEdges.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {matchingEdges.map((edge) => (
                  <div key={`${edge.source}-${edge.target}-${edge.type}`} className="rounded-lg bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
                    <span className="font-medium text-[var(--color-foreground)]">{edge.label}</span>
                    {" - "}
                    {edge.type.replaceAll("_", " ")}
                    {" - "}
                    weight {edge.weight}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RelationshipMetricCard({
  label,
  value,
  description,
}: {
  label: string;
  value: number;
  description: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <p className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{value}</p>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{description}</p>
    </div>
  );
}

function isRenderableRelationshipGraph(graph: CrmRelationshipGraph | null): graph is CrmRelationshipGraph {
  return Boolean(
    graph
    && Array.isArray(graph.nodes)
    && Array.isArray(graph.edges)
    && Array.isArray(graph.connectors)
    && graph.nodes.length > 0,
  );
}

export function getCrmRelationshipInfluenceScore(node: CrmRelationshipNode) {
  const leadScore = node.metadata?.leadScore ?? 0;
  const referralCount = node.metadata?.referralCount ?? 0;
  const dealValueScore = Math.round((node.metadata?.dealValueCents ?? 0) / 10_000);
  const connectorBonus = node.metadata?.isConnector ? 25 : 0;

  return leadScore + referralCount * 15 + dealValueScore + connectorBonus;
}
